import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { usePublicTheme } from '../hooks/usePublicTheme';
import { useLanguage } from '../contexts/LanguageContext';
import DigitalSignature from '../components/common/DigitalSignature';
import {
  SunIcon, MoonIcon, ShieldCheckIcon, ClipboardDocumentListIcon, MapPinIcon, CalendarIcon, ClockIcon,
  DocumentArrowDownIcon, PencilIcon, CheckCircleIcon, BuildingOffice2Icon, UserIcon, EnvelopeIcon, PhoneIcon,
  ExclamationTriangleIcon, WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

/* ---------- típusok (a Cloud Function publikus válasza) ---------- */
interface PubItem { id: string; name: string; quantity: number; unit: string; unitPrice: number; vatRate: number; totalNet: number; totalGross: number; warranty: string }
interface PubParty { name: string; address?: { street?: string; city?: string; postalCode?: string; country?: string } | null; taxNumber?: string; email?: string; phone?: string; logoUrl?: string | null; website?: string }
interface PubSignature { id: string; signerName: string; signerRole: string; signatureData: string; signedAt: string | null }
interface PubPhoto { id: string; url: string; phase: 'before' | 'after' | 'other'; caption: string }
interface PubWorksheet {
  id: string; number: string; title: string; status: string; workType: string; location: string; description: string; workDetails: string;
  worksheetDate: string | null; startTime: string; endTime: string; actualHours: number; warranty: string;
  items: PubItem[]; totals: { totalNet: number; totalVat: number; totalGross: number };
  notes: { id: string; text: string; createdAt: string | null }[]; signatures: PubSignature[]; photos?: PubPhoto[];
  organization: PubParty; customer: PubParty;
  feedback?: { rating: number; comment: string; at: string | null } | null;
}

const api = {
  get: httpsCallable<{ token: string }, { worksheet: PubWorksheet }>(functions, 'publicWorksheetGet'),
  sign: httpsCallable<{ token: string; signerName: string; signerRole: string; signatureData: string }, { worksheet: PubWorksheet }>(functions, 'publicWorksheetSign'),
  feedback: httpsCallable<{ token: string; rating: number; comment: string; name: string }, { worksheet: PubWorksheet }>(functions, 'publicWorksheetFeedback')
};

const STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Vázlat', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  assigned: { label: 'Kiszignált', cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  in_progress: { label: 'Folyamatban', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  review_pending: { label: 'Ellenőrzésre vár', cls: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  completed: { label: 'Befejezve', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  sent: { label: 'Elküldve', cls: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' },
  partially_invoiced: { label: 'Részben számlázva', cls: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  invoiced: { label: 'Számlázva', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  cancelled: { label: 'Törölve', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  hibernated: { label: 'Hibernált', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
};
const WORK_TYPES: Record<string, string> = { installation: 'Telepítés', maintenance: 'Karbantartás', repair: 'Javítás', inspection: 'Ellenőrzés', other: 'Egyéb' };

const money = (n: number) => new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso: string | null, withTime = false) => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '–';
  const base = `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`;
  return withTime ? `${base} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : base;
};
const addr = (a?: PubParty['address']) => (a ? [[a.postalCode, a.city].filter(Boolean).join(' '), a.street].filter(Boolean).join(', ') : '');

const card = 'bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c]';
const btnPrimary = 'inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#0f7f75] text-white text-sm font-medium hover:bg-[#0c6b62] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-gray-300 dark:border-[#2a2f3c] text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50';
const sectionTitle = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3';

const PublicWorksheetView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { theme, toggle: toggleTheme } = usePublicTheme();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ws, setWs] = useState<PubWorksheet | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [fbRating, setFbRating] = useState(0);
  const [fbHover, setFbHover] = useState(0);
  const [fbComment, setFbComment] = useState('');
  const [fbName, setFbName] = useState('');
  const [fbBusy, setFbBusy] = useState(false);
  const [fbEdit, setFbEdit] = useState(false);
  const sendFeedback = async () => {
    if (!token || !fbRating) return;
    setFbBusy(true);
    try {
      const res = await api.feedback({ token, rating: fbRating, comment: fbComment, name: fbName || (ws?.signatures?.[0]?.signerName || '') });
      setWs(res.data.worksheet); setFbEdit(false);
      toast.success('Köszönjük a visszajelzést!');
    } catch (e: any) { toast.error(e?.message?.replace(/^.*?:\s*/, '') || 'Nem sikerült elküldeni'); }
    finally { setFbBusy(false); }
  };

  useEffect(() => {
    if (!token) { setError('Érvénytelen link'); setLoading(false); return; }
    api.get({ token })
      .then(res => setWs(res.data.worksheet))
      .catch((e: any) => setError(e?.message?.replace(/^.*?:\s*/, '') || 'A munkalap nem érhető el'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { if (ws) document.title = `Munkalap ${ws.number} – ${ws.organization?.name || ''}`; }, [ws]);

  const handleSignature = async (signatureData: string, signerName: string, signerRole: string) => {
    if (!token) return;
    try {
      setSignBusy(true);
      const res = await api.sign({ token, signerName, signerRole: signerRole || 'Ügyfél', signatureData });
      setWs(res.data.worksheet);
      setShowSignature(false);
    } catch (e: any) {
      alert(e?.message?.replace(/^.*?:\s*/, '') || 'Az aláírás rögzítése nem sikerült');
    } finally {
      setSignBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!ws) return;
    try {
      setPdfBusy(true);
      const { generateWorksheetPdf } = await import('../lib/worksheetPdfGenerator');
      const worksheetLike: any = {
        id: ws.id, worksheetNumber: ws.number, title: ws.title, status: ws.status, workType: ws.workType, location: ws.location,
        description: ws.description, workDetails: ws.workDetails, warranty: ws.warranty, actualHours: ws.actualHours,
        worksheetDate: ws.worksheetDate ? new Date(ws.worksheetDate) : null, startTime: ws.startTime, endTime: ws.endTime,
        createdAt: ws.worksheetDate ? new Date(ws.worksheetDate) : new Date(), updatedAt: new Date(),
        items: ws.items.map((i, idx) => ({ ...i, type: 'service', order: idx })),
        notes_list: ws.notes.map(n => ({ ...n, visibility: 'customer', createdBy: '', createdAt: n.createdAt ? new Date(n.createdAt) : new Date() })),
        signatures: ws.signatures.map(s => ({ ...s, signedBy: s.signerName, signedAt: s.signedAt ? new Date(s.signedAt) : new Date() })),
        organizationId: ''
      };
      const { urlToDataUrl } = await import('../lib/imageCompress');
      const photos: { dataUrl: string; label: string; caption?: string }[] = [];
      for (const p of ws.photos || []) { const d = await urlToDataUrl(p.url, 900); if (d) photos.push({ dataUrl: d, label: p.phase === 'before' ? 'Előtte' : p.phase === 'after' ? 'Utána' : 'Fotó', caption: p.caption }); }
      generateWorksheetPdf({ worksheet: worksheetLike, timeEntries: [], customerName: ws.customer.name || '', t, photos });
    } catch (e) {
      console.error(e);
      alert('A PDF létrehozása nem sikerült');
    } finally {
      setPdfBusy(false);
    }
  };

  const Shell: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
    <div className="min-h-screen bg-[#f7f7f9] dark:bg-[#0b0d12] text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className={`${wide ? 'max-w-4xl' : 'max-w-md'} mx-auto px-4 py-6 sm:py-10`}>
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <ShieldCheckIcon className="w-3.5 h-3.5" /> Bizalmas dokumentum – kérjük, ne ossza meg illetéktelenekkel.
          </div>
          <button type="button" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
            className="w-9 h-9 rounded-lg border border-gray-200 dark:border-[#2a2f3c] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center flex-shrink-0">
            {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
          </button>
        </div>
        {children}
        <p className="mt-8 text-center text-[11px] text-gray-400 dark:text-gray-500">Készült a YouCRM rendszerrel. Fejleszti a Webissimo Kft.</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className={`${card} p-8 text-center`}>
          <div className="mx-auto h-8 w-8 rounded-full border-2 border-[#0f7f75] border-t-transparent animate-spin" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Munkalap betöltése…</p>
        </div>
      </Shell>
    );
  }

  if (error || !ws) {
    return (
      <Shell>
        <div className={`${card} p-8 text-center`}>
          <div className="mx-auto w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="mt-4 text-lg font-semibold">Munkalap nem található</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error || 'A link lehet, hogy lejárt vagy érvénytelen.'}</p>
        </div>
      </Shell>
    );
  }

  const st = STATUS[ws.status] || STATUS.draft;

  return (
    <Shell wide>
      {/* Fejléc */}
      <div className={`${card} overflow-hidden`}>
        <div className="bg-[#0f7f75] text-white px-5 sm:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {ws.organization.logoUrl
                ? <img src={ws.organization.logoUrl} alt="" className="h-12 w-12 rounded-lg bg-white object-contain p-1 flex-shrink-0" />
                : <div className="h-12 w-12 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0"><ClipboardDocumentListIcon className="w-6 h-6" /></div>}
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-white/70">Munkalap</div>
                <h1 className="text-xl sm:text-2xl font-semibold leading-tight truncate">{ws.title}</h1>
                <div className="text-sm text-white/80 mt-0.5">{ws.number}</div>
              </div>
            </div>
            <span className={`self-start inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
          </div>
        </div>

        <div className="p-5 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className={sectionTitle}>Kiállító</div>
            <div className="flex items-start gap-2.5">
              <BuildingOffice2Icon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium">{ws.organization.name || '–'}</div>
                {addr(ws.organization.address) && <div className="text-gray-500 dark:text-gray-400">{addr(ws.organization.address)}</div>}
                {ws.organization.taxNumber && <div className="text-gray-500 dark:text-gray-400">Adószám: {ws.organization.taxNumber}</div>}
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-gray-500 dark:text-gray-400">
                  {ws.organization.email && <span className="inline-flex items-center gap-1"><EnvelopeIcon className="w-3.5 h-3.5" />{ws.organization.email}</span>}
                  {ws.organization.phone && <span className="inline-flex items-center gap-1"><PhoneIcon className="w-3.5 h-3.5" />{ws.organization.phone}</span>}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={sectionTitle}>Ügyfél</div>
            <div className="flex items-start gap-2.5">
              <UserIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium">{ws.customer.name || '–'}</div>
                {addr(ws.customer.address) && <div className="text-gray-500 dark:text-gray-400">{addr(ws.customer.address)}</div>}
              </div>
            </div>
            {ws.location && (
              <div className="flex items-start gap-2.5 mt-3">
                <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm"><div className="text-gray-500 dark:text-gray-400 text-xs">Munkavégzés helye</div><div>{ws.location}</div></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Adatok */}
      <div className={`${card} mt-4 p-5 sm:p-8`}>
        <div className={sectionTitle}>Munka adatai</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <WrenchScrewdriverIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div><div className="text-xs text-gray-500 dark:text-gray-400">Típus</div><div>{WORK_TYPES[ws.workType] || ws.workType}</div></div>
          </div>
          <div className="flex items-start gap-2">
            <CalendarIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div><div className="text-xs text-gray-500 dark:text-gray-400">Munka dátuma</div><div>{fmtDate(ws.worksheetDate)}</div></div>
          </div>
          {(ws.startTime || ws.endTime) && (
            <div className="flex items-start gap-2">
              <ClockIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div><div className="text-xs text-gray-500 dark:text-gray-400">Időtartam</div><div>{ws.startTime || '?'} – {ws.endTime || '?'}</div></div>
            </div>
          )}
          {ws.actualHours > 0 && (
            <div className="flex items-start gap-2">
              <ClockIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div><div className="text-xs text-gray-500 dark:text-gray-400">Munkaóra</div><div>{ws.actualHours} óra</div></div>
            </div>
          )}
        </div>
        {(ws.description || ws.workDetails) && (
          <div className="mt-5 space-y-3 text-sm">
            {ws.description && <div><div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Leírás</div><p className="whitespace-pre-wrap">{ws.description}</p></div>}
            {ws.workDetails && <div><div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Elvégzett munka</div><p className="whitespace-pre-wrap">{ws.workDetails}</p></div>}
          </div>
        )}
      </div>

      {/* Tételek */}
      {ws.items.length > 0 && (
        <div className={`${card} mt-4 overflow-hidden`}>
          <div className="px-5 sm:px-8 pt-5 sm:pt-6"><div className={sectionTitle}>Tételek</div></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 border-y border-gray-200 dark:border-[#2a2f3c] bg-gray-50 dark:bg-white/[0.03]">
                  <th className="text-left font-medium px-5 sm:px-8 py-2.5">Megnevezés</th>
                  <th className="text-right font-medium px-3 py-2.5">Menny.</th>
                  <th className="text-right font-medium px-3 py-2.5 hidden sm:table-cell">Egységár</th>
                  <th className="text-right font-medium px-3 py-2.5 hidden sm:table-cell">ÁFA</th>
                  <th className="text-right font-medium px-5 sm:px-8 py-2.5">Nettó</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#2a2f3c]">
                {ws.items.map(item => (
                  <tr key={item.id}>
                    <td className="px-5 sm:px-8 py-2.5">
                      <div className="font-medium">{item.name}</div>
                      {item.warranty && <div className="text-xs text-gray-500 dark:text-gray-400">Garancia: {item.warranty}</div>}
                    </td>
                    <td className="text-right px-3 py-2.5 whitespace-nowrap">{item.quantity} {item.unit}</td>
                    <td className="text-right px-3 py-2.5 whitespace-nowrap hidden sm:table-cell">{money(item.unitPrice)}</td>
                    <td className="text-right px-3 py-2.5 whitespace-nowrap hidden sm:table-cell">{item.vatRate}%</td>
                    <td className="text-right px-5 sm:px-8 py-2.5 whitespace-nowrap font-medium">{money(item.totalNet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 sm:px-8 py-5 border-t border-gray-200 dark:border-[#2a2f3c] flex justify-end">
            <div className="w-full sm:w-72 text-sm space-y-1.5">
              <div className="flex justify-between text-gray-600 dark:text-gray-300"><span>Nettó összesen</span><span>{money(ws.totals.totalNet)}</span></div>
              <div className="flex justify-between text-gray-600 dark:text-gray-300"><span>ÁFA</span><span>{money(ws.totals.totalVat)}</span></div>
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200 dark:border-[#2a2f3c]"><span>Bruttó végösszeg</span><span className="text-[#0f7f75]">{money(ws.totals.totalGross)}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Fotók */}
      {ws.photos && ws.photos.length > 0 && (
        <div className={`${card} mt-4 p-5 sm:p-8`}>
          <div className={sectionTitle}>Fotódokumentáció</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {ws.photos.map(p => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="relative block aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-[#2a2f3c] bg-gray-100 dark:bg-black/30">
                <img src={p.url} alt={p.caption || ''} className="w-full h-full object-cover" loading="lazy" />
                <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white ${p.phase === 'before' ? 'bg-amber-500/90' : p.phase === 'after' ? 'bg-emerald-600/90' : 'bg-gray-700/80'}`}>{p.phase === 'before' ? 'Előtte' : p.phase === 'after' ? 'Utána' : 'Fotó'}</span>
                {p.caption && <span className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 text-[11px] bg-black/55 text-white truncate">{p.caption}</span>}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Megjegyzések */}
      {ws.notes.length > 0 && (
        <div className={`${card} mt-4 p-5 sm:p-8`}>
          <div className={sectionTitle}>Megjegyzések</div>
          <div className="space-y-2">
            {ws.notes.map(n => (
              <div key={n.id} className="text-sm rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-[#2a2f3c] px-3 py-2.5 whitespace-pre-wrap">{n.text}</div>
            ))}
          </div>
        </div>
      )}

      {/* Aláírások */}
      <div className={`${card} mt-4 p-5 sm:p-8`}>
        <div className={sectionTitle}>Aláírások</div>
        {ws.signatures.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {ws.signatures.map(sig => (
              <div key={sig.id} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-[#2a2f3c] p-3">
                <img src={sig.signatureData} alt="Aláírás" className="h-14 w-28 object-contain rounded bg-white border border-gray-100 dark:border-[#2a2f3c] flex-shrink-0" />
                <div className="min-w-0 text-sm">
                  <div className="font-medium truncate">{sig.signerName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{sig.signerRole}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">{fmtDate(sig.signedAt, true)}</div>
                </div>
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 ml-auto flex-shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Még nincs aláírás. Az elvégzett munka átvételét az alábbi gombbal igazolhatja.</p>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <button type="button" onClick={() => setShowSignature(true)} disabled={signBusy} className={btnPrimary}>
            <PencilIcon className="w-4 h-4" /> Aláírás hozzáadása
          </button>
          <button type="button" onClick={downloadPdf} disabled={pdfBusy} className={btnSecondary}>
            <DocumentArrowDownIcon className="w-4 h-4" /> {pdfBusy ? 'PDF készül…' : 'PDF letöltése'}
          </button>
        </div>
        <DigitalSignature isOpen={showSignature} onClose={() => setShowSignature(false)} onSave={handleSignature} />
      </div>

      {/* Ügyfél visszajelzés */}
      <div className={`${card} mt-4 p-5 sm:p-8`}>
        <div className={sectionTitle}>Hogy volt megelégedve a munkával?</div>
        {ws.feedback && !fbEdit ? (
          <div className="flex items-start gap-3">
            <div className="text-2xl leading-none text-amber-400 tracking-tight">{'★'.repeat(ws.feedback.rating)}<span className="text-gray-300 dark:text-gray-600">{'★'.repeat(5 - ws.feedback.rating)}</span></div>
            <div className="min-w-0 text-sm">
              {ws.feedback.comment && <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{ws.feedback.comment}</p>}
              <div className="text-xs text-gray-400 mt-1">Köszönjük! {ws.feedback.at ? fmtDate(ws.feedback.at, true) : ''} · <button type="button" onClick={() => { setFbRating(ws.feedback!.rating); setFbComment(ws.feedback!.comment || ''); setFbEdit(true); }} className="underline">módosítás</button></div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-1" onMouseLeave={() => setFbHover(0)}>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => setFbRating(n)} onMouseEnter={() => setFbHover(n)} aria-label={`${n} csillag`} className={`text-4xl leading-none transition-colors ${(fbHover || fbRating) >= n ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}>★</button>
              ))}
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{['', 'Nagyon elégedetlen', 'Elégedetlen', 'Elmegy', 'Elégedett', 'Kiváló'][fbHover || fbRating] || 'Koppintson egy csillagra'}</span>
            </div>
            {fbRating > 0 && (
              <>
                <textarea value={fbComment} onChange={e => setFbComment(e.target.value)} rows={3} placeholder={fbRating <= 3 ? 'Mi nem volt rendben? Segít, hogy javítsunk.' : 'Megjegyzés (nem kötelező)'} className="w-full rounded-lg border border-gray-300 dark:border-[#2a2f3c] bg-white dark:bg-[#0f1117] px-3 py-2 text-sm" />
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={fbName} onChange={e => setFbName(e.target.value)} placeholder="Az Ön neve (nem kötelező)" className="flex-1 rounded-lg border border-gray-300 dark:border-[#2a2f3c] bg-white dark:bg-[#0f1117] px-3 h-10 text-sm" />
                  <button type="button" onClick={sendFeedback} disabled={fbBusy} className={btnPrimary}>{fbBusy ? 'Küldés…' : 'Értékelés küldése'}</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
};

export default PublicWorksheetView;
